import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Key, 
  Shield, 
  CheckCircle, 
  XCircle, 
  ExternalLink, 
  CreditCard, 
  Brain, 
  Zap,
  Eye,
  Globe,
  Mail,
  Info
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface OrgWithSettings {
  id: string;
  name: string;
  slug: string;
  settings: {
    enable_integrations: boolean | null;
    stripe_publishable_key: string | null;
    openai_api_key: string | null;
    zapier_webhook_url: string | null;
  } | null;
}

// Platform-level secrets (stored in Lovable Cloud)
const PLATFORM_SECRETS = [
  { name: "STRIPE_SECRET_KEY", label: "Stripe Secret Key", icon: CreditCard, description: "Used for processing payments" },
  { name: "OPENAI_API_KEY", label: "OpenAI API Key", icon: Brain, description: "Used for embeddings and AI features" },
  { name: "MAGISTERIUM_API_KEY", label: "Magisterium API Key", icon: Brain, description: "Used for theological AI context" },
  { name: "RESEND_API_KEY", label: "Resend API Key", icon: Mail, description: "Used for sending team invitation emails" },
  { name: "ZAPIER_SECRET", label: "Zapier Secret", icon: Zap, description: "Used for authenticating Zapier webhooks" },
  { name: "LOVABLE_API_KEY", label: "Lovable AI Key", icon: Brain, description: "Auto-managed by Lovable Cloud" },
];

const SuperAdminIntegrations: React.FC = () => {
  const navigate = useNavigate();

  // Fetch all organizations with their settings
  const { data: organizations, isLoading } = useQuery({
    queryKey: ['super-admin-org-integrations'],
    queryFn: async () => {
      const { data: orgs, error: orgsError } = await supabase
        .from('organizations')
        .select('id, name, slug')
        .order('name');
      
      if (orgsError) throw orgsError;

      // Fetch settings for all orgs
      const { data: allSettings, error: settingsError } = await supabase
        .from('organization_settings')
        .select('organization_id, enable_integrations, stripe_publishable_key, openai_api_key, zapier_webhook_url');
      
      if (settingsError) throw settingsError;

      const settingsMap = new Map(allSettings?.map(s => [s.organization_id, s]));

      return orgs.map(org => ({
        ...org,
        settings: settingsMap.get(org.id) || null
      })) as OrgWithSettings[];
    }
  });

  const hasOrgOverride = (settings: OrgWithSettings['settings'], key: keyof NonNullable<OrgWithSettings['settings']>) => {
    return settings && settings[key] && String(settings[key]).trim() !== '';
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold font-display">Platform Integrations</h1>
        <p className="text-muted-foreground">Manage platform-wide API keys and view organization overrides</p>
      </div>

      {/* Platform-Wide GTM - Read Only */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-sky-600" />
            Google Tag Manager
          </CardTitle>
          <CardDescription>
            Platform-wide GTM is configured in the application source code for maximum reliability.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-slate-50 border">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              <span className="font-medium text-slate-800">GTM Container Active</span>
            </div>
            <code className="text-sm font-mono text-slate-700">GTM-M6F6BJ6W</code>
          </div>
          
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Static Configuration</AlertTitle>
            <AlertDescription>
              GTM is configured directly in <code className="bg-slate-100 px-1 rounded">index.html</code> for 
              reliability and Google verification compatibility. To change the container ID, update the 
              file directly in the codebase or contact the development team.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Platform-Wide Secrets */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-sky-600" />
            Platform-Wide Secrets
          </CardTitle>
          <CardDescription>
            These secrets are stored securely in Lovable Cloud and apply to all organizations by default.
            Organizations can override some of these with their own keys.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {PLATFORM_SECRETS.map((secret) => (
              <div 
                key={secret.name}
                className="flex items-center justify-between p-3 rounded-lg border bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  <secret.icon className="h-5 w-5 text-slate-600" />
                  <div>
                    <div className="font-medium text-slate-800">{secret.label}</div>
                    <div className="text-sm text-slate-500">{secret.description}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-600/30 bg-emerald-500/10">
                    <CheckCircle className="h-3 w-3" />
                    Configured
                  </Badge>
                  {secret.name !== "LOVABLE_API_KEY" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open('https://lovable.dev', '_blank');
                      }}
                      className="gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Update in Cloud
                    </Button>
                  )}
                  {secret.name === "LOVABLE_API_KEY" && (
                    <span className="text-xs text-slate-500">Auto-managed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organization-Specific Settings Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-sky-600" />
            Organization API Settings
          </CardTitle>
          <CardDescription>
            View which organizations have custom API key overrides configured. 
            When set, organization-specific keys take precedence over platform defaults.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-slate-500">Loading organizations...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead className="text-center">Integrations Enabled</TableHead>
                  <TableHead className="text-center">Stripe</TableHead>
                  <TableHead className="text-center">OpenAI</TableHead>
                  <TableHead className="text-center">Zapier</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations?.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{org.name}</div>
                        <div className="text-xs text-slate-500">/{org.slug}</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {org.settings?.enable_integrations ? (
                        <Badge variant="default" className="bg-sky-600">Enabled</Badge>
                      ) : (
                        <Badge variant="outline" className="text-slate-500">Disabled</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {hasOrgOverride(org.settings, 'stripe_publishable_key') ? (
                        <CheckCircle className="h-4 w-4 text-emerald-600 mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-slate-300 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {hasOrgOverride(org.settings, 'openai_api_key') ? (
                        <CheckCircle className="h-4 w-4 text-emerald-600 mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-slate-300 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {hasOrgOverride(org.settings, 'zapier_webhook_url') ? (
                        <CheckCircle className="h-4 w-4 text-emerald-600 mx-auto" />
                      ) : (
                        <XCircle className="h-4 w-4 text-slate-300 mx-auto" />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/super-admin/organizations/${org.id}`)}
                        className="gap-1"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminIntegrations;
